// QualityProfiles.js - Performance quality profiles for different device capabilities

export const QualityProfiles = {
  HIGH_END: {
    camera: {
      width: 1920,
      height: 1080,
      frameRate: 60
    },
    segmentation: {
      width: 512,
      height: 288,
      processEveryFrame: 1
    },
    pose: {
      processEveryFrame: 1,
      delegate: 'GPU',
      modelComplexity: 'heavy',  // Best accuracy for high-end devices
      minPoseDetectionConfidence: 0.5,
      minPosePresenceConfidence: 0.5,
      minTrackingConfidence: 0.5
    },
    rendering: {
      pixelRatio: 1,
      shadowMapSize: 1024,
      antialiasing: true,
      disableShadows: false,
      shadowDistance: 40
    }
  },

  MEDIUM: {
    camera: {
      width: 1280,
      height: 720,
      frameRate: 30
    },
    segmentation: {
      width: 384,
      height: 216,
      processEveryFrame: 2
    },
    pose: {
      processEveryFrame: 2,
      delegate: 'GPU',
      modelComplexity: 'full',  // Balance of speed and accuracy
      minPoseDetectionConfidence: 0.4,
      minPosePresenceConfidence: 0.4,
      minTrackingConfidence: 0.4
    },
    rendering: {
      pixelRatio: 0.8,
      shadowMapSize: 512,
      antialiasing: true,
      disableShadows: false,
      shadowDistance: 30
    }
  },

  // For devices with good CPU but incompatible GPU (Mali-G71, Mali-G52, etc.)
  // Optimized for Chrome on Android which has poor MediaPipe performance
  MEDIUM_CPU: {
    camera: {
      width: 1280,
      height: 720,
      frameRate: 30
    },
    segmentation: {
      width: 192,            // Reduced for Mali GPU performance (44% fewer pixels than 256x144)
      height: 108,           // Reduced for Mali GPU performance
      processEveryFrame: 2   // Process every 2 frames to reduce CPU load
    },
    pose: {
      processEveryFrame: 1,  // Process every frame for smooth gameplay
      delegate: 'CPU',       // Use CPU instead of problematic GPU
      modelComplexity: 'lite',  // Use LITE model instead of HEAVY (3-5x faster)
      minPoseDetectionConfidence: 0.3,  // Lower threshold for LITE model
      minPosePresenceConfidence: 0.3,   // Lower threshold for LITE model
      minTrackingConfidence: 0.3        // Lower threshold for LITE model
    },
    rendering: {
      pixelRatio: 0.75,      // Slightly reduced for better performance
      shadowMapSize: 512,
      antialiasing: true,
      disableShadows: false,
      shadowDistance: 20
    }
  },

  LOW_END: {
    camera: {
      width: 640,
      height: 480,
      frameRate: 30
    },
    segmentation: {
      width: 256,
      height: 144,
      processEveryFrame: 3
    },
    pose: {
      processEveryFrame: 3,
      delegate: 'CPU',
      modelComplexity: 'lite',  // Fastest model for low-end devices
      minPoseDetectionConfidence: 0.3,
      minPosePresenceConfidence: 0.3,
      minTrackingConfidence: 0.3
    },
    rendering: {
      pixelRatio: 0.6,
      shadowMapSize: 256,
      antialiasing: false,
      disableShadows: true,
      shadowDistance: 20
    }
  },

  // Ultra low-end for very constrained devices
  ULTRA_LOW_END: {
    camera: {
      width: 480,
      height: 360,
      frameRate: 24
    },
    segmentation: {
      width: 192,
      height: 108,
      processEveryFrame: 4
    },
    pose: {
      processEveryFrame: 4,
      delegate: 'CPU',
      modelComplexity: 'lite',  // Fastest model for ultra-low-end devices
      minPoseDetectionConfidence: 0.25,
      minPosePresenceConfidence: 0.25,
      minTrackingConfidence: 0.25
    },
    rendering: {
      pixelRatio: 0.5,
      shadowMapSize: 128,
      antialiasing: false,
      disableShadows: true,
      shadowDistance: 15
    }
  }
};

// Global quality profile state - lazy initialization to avoid import issues
let currentQualityProfile = null;
let deviceCapabilitiesCache = null;

function ensureInitialized() {
  if (currentQualityProfile) return;

  try {
    // Lazy import to avoid circular dependency issues
    if (!deviceCapabilitiesCache) {
      const DeviceDetection = require('../utils/DeviceDetection.js');
      deviceCapabilitiesCache = DeviceDetection.deviceCapabilities;
    }

    currentQualityProfile = QualityProfiles[deviceCapabilitiesCache.recommendedQuality] || QualityProfiles.MEDIUM;

    console.log(`🎮 Quality profile initialized: ${deviceCapabilitiesCache.recommendedQuality}`, currentQualityProfile);
    console.log(`🎮 Device GPU detected: ${deviceCapabilitiesCache.gpu.renderer} (${deviceCapabilitiesCache.gpu.tier})`);
    console.log(`🎮 Using delegate: ${currentQualityProfile.pose.delegate}`);
  } catch (error) {
    console.warn('[QualityProfiles] Could not load device detection, using MEDIUM profile:', error);
    currentQualityProfile = QualityProfiles.MEDIUM;
  }
}

export function getQualityProfile() {
  ensureInitialized();
  return currentQualityProfile;
}

export function setQualityProfile(profileName) {
  if (QualityProfiles[profileName]) {
    currentQualityProfile = QualityProfiles[profileName];
    console.log(`Quality profile set to: ${profileName}`, currentQualityProfile);
  } else {
    console.warn(`Unknown quality profile: ${profileName}, using MEDIUM`);
    currentQualityProfile = QualityProfiles.MEDIUM;
  }
  return currentQualityProfile;
}

// Allow manual override for testing
export function overrideQualityProfile(customProfile) {
  currentQualityProfile = { ...QualityProfiles.MEDIUM, ...customProfile };
  console.log('Quality profile overridden:', currentQualityProfile);
  return currentQualityProfile;
}

// Performance monitoring utilities
export const PerformanceMonitor = {
  metrics: {
    fps: 0,
    frameDrops: 0,
    memoryUsage: 0,
    averageFrameTime: 0,
    lastFrameTime: 0
  },

  measurePerformance() {
    const now = performance.now();
    const frameTime = now - this.metrics.lastFrameTime;
    const fps = 1000 / frameTime;

    // Update metrics
    this.metrics.fps = fps;
    this.metrics.averageFrameTime = (this.metrics.averageFrameTime * 0.9) + (frameTime * 0.1);

    if (fps < 20) {
      this.metrics.frameDrops++;
    }

    // Memory usage (if available)
    if (performance.memory) {
      this.metrics.memoryUsage = performance.memory.usedJSHeapSize / (1024 * 1024); // MB
    }

    this.metrics.lastFrameTime = now;

    // Auto-adjust quality if performance is SEVERELY poor
    // More lenient threshold - only suggest downgrade at critically low FPS
    if (this.metrics.frameDrops > 30 && fps < 10) {
      this.suggestQualityDowngrade();
    }

    return this.metrics;
  },

  suggestQualityDowngrade() {
    ensureInitialized();
    const current = getQualityProfile();

    // Don't suggest downgrades for MEDIUM_CPU - it's already optimized for CPU
    if (current === QualityProfiles.MEDIUM_CPU) {
      // Silent - MEDIUM_CPU is expected to have slightly lower FPS
      return null;
    }

    if (current === QualityProfiles.HIGH_END) {
      console.warn('⚠️ Performance issues detected, consider switching to MEDIUM quality');
      return 'MEDIUM';
    } else if (current === QualityProfiles.MEDIUM) {
      console.warn('⚠️ Performance issues detected, consider switching to MEDIUM_CPU or LOW_END quality');
      return 'MEDIUM_CPU';
    } else if (current === QualityProfiles.LOW_END) {
      console.warn('⚠️ Severe performance issues detected, consider switching to ULTRA_LOW_END quality');
      return 'ULTRA_LOW_END';
    }

    return null;
  },

  reset() {
    this.metrics.frameDrops = 0;
    this.metrics.lastFrameTime = performance.now();
  }
};
